import { useEffect, useRef, useState } from 'preact/hooks';
import type { SetpointStepperWidget } from '../widgets';
import type { IPointBindingSource } from '../binding';
import { pointSignal } from '../pointSignal';
import { isDegraded } from '../quality';
import { useLeafMeta } from '../LeafMetaContext';
import { formatNumber } from '../../format/leafValue';
import './SetpointStepperRenderer.css';

interface Props {
  widget: SetpointStepperWidget;
  source: IPointBindingSource;
}

/**
 * Numeric setpoint with +/- buttons. Displays the feedback value as the
 * "big number"; the just-pressed-but-not-yet-confirmed value overlays as a
 * pending marker. Hold-to-repeat (concept §3) fires at `repeatDelayMs` while
 * the button stays pressed — pointerup / blur cancels.
 */
export function SetpointStepperRenderer({ widget, source }: Props) {
  const { value, release } = pointSignal(source, widget.feedbackPoint);
  useEffect(() => release, [release]);
  const meta = useLeafMeta(widget.feedbackPoint);

  const [pending, setPending] = useState<{ target: number; state: 'sending' | 'rejected' } | null>(null);
  const [target, setTarget] = useState<number | null>(null);
  const commitTimerRef = useRef<number | null>(null);
  const holdTimerRef = useRef<number | null>(null);

  const current = value.value;
  const stale = isDegraded(current);
  const feedbackNumber = typeof current?.value === 'number' && Number.isFinite(current.value)
    ? current.value
    : null;

  const displayValue = target ?? feedbackNumber;

  // Debounced commit: bump the target on button press, actually write when
  // the operator settles for 400 ms. Avoids spamming 15 write commands when
  // the user holds "+".
  useEffect(() => {
    if (target === null) return;
    if (commitTimerRef.current !== null) window.clearTimeout(commitTimerRef.current);
    commitTimerRef.current = window.setTimeout(() => {
      commitTimerRef.current = null;
      void writeTarget(target);
    }, 400);
    return () => {
      if (commitTimerRef.current !== null) {
        window.clearTimeout(commitTimerRef.current);
        commitTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  async function writeTarget(v: number) {
    setPending({ target: v, state: 'sending' });
    const ac = new AbortController();
    try {
      for await (const ack of source.write(widget.commandPoint, v, ac.signal)) {
        if (ack.state === 'confirmed') {
          setPending(null);
          setTarget(null);
          return;
        }
        if (ack.state === 'rejected') {
          setPending({ target: v, state: 'rejected' });
          return;
        }
      }
      setPending(null);
    } catch {
      setPending(null);
    }
  }

  function bump(delta: number): void {
    setTarget((prev) => {
      const base = prev ?? feedbackNumber ?? widget.min;
      const stepped = round(base + delta, widget.step);
      return clamp(stepped, widget.min, widget.max);
    });
  }

  function startHold(delta: number): void {
    bump(delta);
    if (holdTimerRef.current !== null) window.clearInterval(holdTimerRef.current);
    holdTimerRef.current = window.setInterval(() => bump(delta), widget.repeatDelayMs ?? 250);
  }
  function stopHold(): void {
    if (holdTimerRef.current !== null) {
      window.clearInterval(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  }
  useEffect(() => stopHold, []);

  const decimals = widget.decimals ?? 1;
  // The unit belongs to the leaf; the widget may override it.
  const unit = widget.unit ?? meta?.unit;

  return (
    <div class={`hv-stepper${stale ? ' hv-stepper--stale' : ''}${pending ? ` hv-stepper--${pending.state}` : ''}`}>
      {widget.label && <div class="hv-stepper__label">{widget.label}</div>}
      <div class="hv-stepper__row">
        <button
          type="button"
          class="hv-stepper__btn"
          aria-label="verringern"
          onPointerDown={() => startHold(-widget.step)}
          onPointerUp={stopHold}
          onPointerCancel={stopHold}
          onPointerLeave={stopHold}
          disabled={displayValue !== null && displayValue <= widget.min}
        >
          −
        </button>
        <div class="hv-stepper__readout">
          <span class="hv-stepper__value">
            {displayValue != null ? formatNumber(displayValue, decimals) : '—'}
          </span>
          {unit && <span class="hv-stepper__unit">{unit}</span>}
        </div>
        <button
          type="button"
          class="hv-stepper__btn"
          aria-label="erhöhen"
          onPointerDown={() => startHold(widget.step)}
          onPointerUp={stopHold}
          onPointerCancel={stopHold}
          onPointerLeave={stopHold}
          disabled={displayValue !== null && displayValue >= widget.max}
        >
          +
        </button>
      </div>
      <div class="hv-stepper__meta">
        <span class="muted">Bereich {widget.min}–{widget.max}{unit ? ` ${unit}` : ''}</span>
        {pending && (
          <span class={pending.state === 'rejected' ? 'hv-stepper__pending--rejected' : 'hv-stepper__pending--sending'}>
            {pending.state === 'sending' ? '… gesendet' : '× abgelehnt'}
          </span>
        )}
      </div>
    </div>
  );
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Snap `v` to the nearest step so successive +/- clicks stay on a grid. */
function round(v: number, step: number): number {
  if (step <= 0) return v;
  return Math.round(v / step) * step;
}
